import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React__default, { useContext, Fragment as Fragment$1, Children } from 'react';
import { isFunction, twMerge, isEmpty } from '@belong/common';
import { Stepper } from '../Stepper/Stepper.js';

const WizardBaseContext = /*#__PURE__*/React__default.createContext({
  nextStep: _data => {},
  backStep: _data => {},
  goToStep: _id => {},
  isLastStep: false
});
const WizardBase = ({
  children,
  className = '',
  activeStepId,
  onlyActiveLabel,
  renderNavigator,
  renderHeader,
  onNext,
  onBack,
  onGoToStep,
  onStepSelection,
  stepperContainerClassName // TODO: Please don't use this prop, added for a last minute change before a release and plan remove it.
}) => {
  const {
    activeSteps
  } = WizardBase.getSteps(children);
  const step = activeSteps.find(step => step.props.id === activeStepId);
  const currentStepIndex = step ? activeSteps.indexOf(step) : -1;
  const currentStep = activeSteps[currentStepIndex];
  if (!currentStep) {
    return null;
  }
  const {
    hideNavigator,
    id: currentStepId
  } = currentStep.props || {};
  const isFirstStep = activeSteps.indexOf(currentStep) === 0;
  const isLastStep = activeSteps.indexOf(currentStep) === activeSteps.length - 1;
  const stepItems = activeSteps.filter(step => !handleHideStepper(step.props)).map(step => ({
    label: step.props.label,
    active: step.props.id === currentStepId
  }));
  const shouldHideStepper = handleHideStepper(currentStep.props);
  function handleHideStepper(stepProps) {
    return isFunction(stepProps.hideStepper) && stepProps.hideStepper ? stepProps.hideStepper({
      ...stepProps,
      isStepActive: stepProps.id === currentStepId
    }) : stepProps.hideStepper;
  }
  function nextStep(data) {
    const nextStep = activeSteps[currentStepIndex + 1];
    if (nextStep) {
      onNext?.(nextStep.props, data);
    }
  }
  function backStep(data) {
    const previousStep = activeSteps[currentStepIndex - 1];
    if (previousStep) {
      onBack?.(previousStep.props, data);
    }
  }
  function goToStep(id) {
    const stepIndex = activeSteps.findIndex(step => step.props.id === id);
    const step = activeSteps[stepIndex];
    if (onGoToStep && step) {
      onGoToStep(step.props);
    }
  }
  return jsx(WizardBaseContext.Provider, {
    value: {
      nextStep,
      backStep,
      goToStep,
      isLastStep
    },
    children: jsxs("section", {
      className: className,
      children: [renderHeader && renderHeader({
        ...currentStep.props,
        hideStepper: handleHideStepper(currentStep.props)
      }), !shouldHideStepper && jsx("div", {
        className: 'flex place-content-center',
        children: jsx("div", {
          className: twMerge('w-full md:w-4/5', stepperContainerClassName),
          children: jsx(Stepper, {
            steps: stepItems,
            onStepSelection: onStepSelection,
            onlyActiveLabel: onlyActiveLabel
          })
        })
      }), /*#__PURE__*/React__default.cloneElement(currentStep, {
        currentStepId
      }), !hideNavigator && renderNavigator && renderNavigator({
        isLastStep,
        isFirstStep,
        nextStep,
        backStep
      })]
    })
  });
};
WizardBase.Step = ({
  children,
  ...rest
}) => {
  if (isEmpty(children) || /*#__PURE__*/React__default.isValidElement(children)) {
    return jsx(Fragment, {
      children: children
    });
  }
  if (isFunction(children)) {
    return children(rest);
  }
  return null;
};
WizardBase.useWizard = () => useContext(WizardBaseContext);
WizardBase.getSteps = children => {
  const childrenSteps = children.flatMap(child => {
    if (child.type === Fragment$1) {
      return Children.toArray(child.props.children);
    }
    return [child];
  });
  const wizardSteps = childrenSteps.filter(child => child.type === WizardBase.Step);
  const activeSteps = wizardSteps.filter(child => {
    // if it doesn't have it, it's always true, if it does it should use the property value as it was provided
    return !('isStepActive' in child.props) || child.props.isStepActive;
  });
  return {
    wizardSteps,
    activeSteps
  };
};

export { WizardBase, WizardBaseContext };
